import {
  faCheck,
  faEdit,
  faExclamationCircle,
  faInfoCircle,
  faSortDown,
  faSortUp,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { faComment } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PropertiesStore } from '@hortplus/properties-react';
import { observer } from 'mobx-react';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import {
  Button,
  Col,
  OverlayTrigger,
  Row,
  Table,
  Tooltip
} from 'react-bootstrap';
import URI from 'urijs';
import ConfirmationModal from '../helpers/ConfirmationModal';
import { calcAccumulationThreshold } from '../helpers/TrappingUtils';
import useLocalStorageState from '../helpers/useLocalStorageState';
import { useRelevantTraps } from '../hooks/useRelevantTraps';
import { useStores } from '../stores/useStores';
import EditTrapMonitoringRecordsModal from './EditTrapMonitoringRecordsModal';
import EditTrappingZoneSprayModal from './EditTrappingZoneSprayModal';

const TrappingResultsTable = ({
  trappingZoneResults,
  trappingZone,
  selectedInsectGroup,
  propertiesStore
}) => {
  const { traplogStore } = useStores();
  const [editingTrappingZoneSpray, setEditingTrappingZoneSpray] =
    useState(null);
  const [editingTrappingResult, setEditingTrappingResult] = useState(null);
  const [sortOrder, setSortOrder] = useLocalStorageState(
    'trapping-zone-results-date-order',
    'desc'
  ); // 'desc' | 'asc'.
  const getRelevantTraps = useRelevantTraps();
  const relevantTraps = getRelevantTraps(trappingZone, selectedInsectGroup);

  const deleteTrappingResult = async (trappingResult) => {
    const uri = new URI(
      `${process.env.REACT_APP_TRAPLOG_API_URL}api/trap-monitoring-records`
    );

    trappingResult.trap_monitoring_records.forEach((record) => {
      uri.addQuery('records[]', record.id);
    });

    const res = await fetch(uri.toString(), {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${traplogStore.bearerToken}`,
        SeasonalDatabase: traplogStore.selectedDatabase
      }
    });
    if (res.ok)
      traplogStore.loadTrappingZones(
        traplogStore.bearerToken,
        traplogStore.refreshBearerToken,
        traplogStore.selectedDatabase,
        propertiesStore.selectedPropertyId
      );
    else if (res.status === 401) traplogStore.refreshBearerToken();
    else throw Error(res.statusText);
  };

  const deleteTrappingZoneSpray = async (trappingZoneSprayId) => {
    const res = await fetch(
      `${process.env.REACT_APP_TRAPLOG_API_URL}api/trapping-zone-spray/${trappingZoneSprayId}`,
      {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${traplogStore.bearerToken}`,
          SeasonalDatabase: traplogStore.selectedDatabase
        }
      }
    );
    if (res.ok)
      traplogStore.loadTrappingZones(
        traplogStore.bearerToken,
        traplogStore.refreshBearerToken,
        traplogStore.selectedDatabase,
        propertiesStore.selectedPropertyId
      );
    else if (res.status === 401) traplogStore.refreshBearerToken();
    else throw Error(res.statusText);
  };

  /**
   * props.trappingZoneResults.results ordered by date in descending order.
   */
  const resultsDescending = useMemo(
    () =>
      [...trappingZoneResults.results].sort(
        (a, b) => moment(b.date).valueOf() - moment(a.date).valueOf()
      ),
    [trappingZoneResults]
  );

  /**
   * props.trappingZoneResults.results ordered by date in descending order.
   */
  const resultsAscending = useMemo(
    () =>
      [...trappingZoneResults.results].sort(
        (a, b) => moment(a.date).valueOf() - moment(b.date).valueOf()
      ),
    [trappingZoneResults]
  );

  const trappingResults =
    sortOrder === 'desc' ? resultsDescending : resultsAscending;

  const columns = [
    {
      title: (
        <>
          Date
          <span
            className='px-2'
            onClick={() => {
              if (sortOrder === 'desc') setSortOrder('asc');
              else setSortOrder('desc');
            }}
          >
            {sortOrder === 'desc' ? (
              <FontAwesomeIcon icon={faSortDown} />
            ) : (
              <FontAwesomeIcon icon={faSortUp} />
            )}
          </span>
        </>
      )
    },
    { title: 'Week' },
    { title: 'Cap' },
    { title: 'Base' },
    {
      title: (
        <>
          TH
          <OverlayTrigger
            placement='top'
            overlay={<Tooltip>Threshold</Tooltip>}
          >
            <FontAwesomeIcon icon={faInfoCircle} className='ml-2' size='xs' />
          </OverlayTrigger>
        </>
      )
    },
    ...relevantTraps.map((trap) => ({
      title: trap.code,
      trapId: trap.id,
      trapName: trap.name,
      trapCode: trap.code,
      colSpan: 2,
      style: { borderLeft: '2px solid #dee2e6' }
    })),
    {
      title: (
        <>
          Avg/Week{' '}
          <OverlayTrigger
            placement='top'
            overlay={
              <Tooltip>
                The average number of insects per week across all traps.
              </Tooltip>
            }
          >
            <FontAwesomeIcon icon={faInfoCircle} className='ml-2' size='xs' />
          </OverlayTrigger>
        </>
      ),
      style: { borderLeft: '2px solid #dee2e6' }
    },
    { title: 'Actions' }
  ];

  const tableData = useMemo(() => {
    // Helper functions
    const formatDate = (dt) => ({
      date: dt.format('DD/MM/YYYY'),
      week: dt.format('w')
    });

    const calculateChangeStatus = (changedCount, totalTraps) => {
      if (changedCount === 0) return 'none';
      return changedCount < totalTraps ? 'some' : 'all';
    };

    const calculateAverageAmount = (amounts) => {
      if (!amounts.length) return null;
      const sum = amounts.reduce((acc, val) => acc + val, 0);
      return (sum / amounts.length).toFixed(2);
    };

    const processTraps = (trappingResult, traplogStore) => {
      const amounts = [];
      let amountThresholdsMet = 0;
      let accumulationThresholdsMet = 0;

      const traps = traplogStore.traps.map((trap) => {
        const monitoringRecord = trappingResult.trap_monitoring_records.find(
          (record) => record.trap.id === trap.id
        );

        const trapData = trappingResult.traps[trap.id] || {};
        const { amount = null, accumulation = null } = trapData;

        const amountThresholdMet =
          amount &&
          traplogStore.selectedInsectGroup.per_trap_catch_threshold !== null &&
          amount >= traplogStore.selectedInsectGroup.per_trap_catch_threshold;

        const accumulationThresholdMet =
          accumulation &&
          traplogStore.selectedInsectGroup.accumulation_threshold !== null &&
          accumulation >=
            calcAccumulationThreshold(
              traplogStore.selectedInsectGroup,
              moment(trappingResult.date)
            );

        if (amountThresholdMet) amountThresholdsMet++;
        if (accumulationThresholdMet) accumulationThresholdsMet++;

        amounts.push(amount);

        return {
          id: trap.id,
          name: trap.name,
          code: trap.code,
          amount,
          accumulation,
          comments: monitoringRecord?.comments || null,
          amountThresholdMet,
          accumulationThresholdMet
        };
      });

      return { traps, amounts, amountThresholdsMet, accumulationThresholdsMet };
    };

    // Main processing
    const results = trappingResults.map((trappingResult) => {
      const dt = moment(trappingResult.date);
      const dateInfo = formatDate(dt);

      const { traps, amounts, amountThresholdsMet, accumulationThresholdsMet } =
        processTraps(trappingResult, traplogStore);

      const averageAmount = calculateAverageAmount(amounts);
      const averageThresholdMet =
        traplogStore.selectedInsectGroup.average_threshold !== null &&
        averageAmount >= traplogStore.selectedInsectGroup.average_threshold;

      const luresChanged = trappingResult.trap_monitoring_records.filter(
        (record) => record.lure_changed
      ).length;

      const basesChanged = trappingResult.trap_monitoring_records.filter(
        (record) => record.base_changed
      ).length;

      return {
        ...dateInfo,
        trappingResult,
        capChanges: calculateChangeStatus(luresChanged, traps.length),
        baseChanges: calculateChangeStatus(basesChanged, traps.length),
        sprays: trappingResult.trapping_zone_sprays.map((spray) => ({
          ...dateInfo,
          ...spray
        })),
        record:
          trappingResult.trap_monitoring_records.length > 0
            ? {
                ...dateInfo,
                traps,
                luresChanged,
                basesChanged,
                amountThresholdsMet,
                accumulationThresholdsMet,
                averageThresholdMet,
                averageAmount,
                thresholdMet: trappingResult.threshold_met
              }
            : null,
        sprayed: trappingResult.sprayed
      };
    });

    // Format final page data
    return results.flatMap((result) => {
      const output = [];

      if (result.sprays.length > 0) {
        output.push(
          ...result.sprays.map((spray) => ({
            date: result.date,
            week: result.week,
            spray
          }))
        );
      }

      if (result.record) {
        const trapRecords = {};
        result.record.traps.forEach((trap) => {
          const record = {
            isRecord: true,
            amount: trap.amount,
            accumulation: trap.accumulation,
            comments: trap.comments
          };
          trapRecords[`${trap.name}-${trap.id}`] = record;
        });
        output.push({
          trappingResult: result.trappingResult,
          date: result.date,
          week: result.week,
          capChanges: result.capChanges,
          baseChanges: result.baseChanges,
          threshold: {
            sprayed: result.sprayed,
            amountThresholdsMet: result.record.amountThresholdsMet,
            accumulationThresholdsMet: result.record.accumulationThresholdsMet,
            averageThresholdMet: result.record.averageThresholdMet
          },
          ...trapRecords,
          averageAmount: result.record.averageAmount
        });
      }
      return output;
    });
  }, [trappingResults, relevantTraps, selectedInsectGroup]);

  const rowHasRecords = useCallback((row) => {
    return Object.keys(row).some((key) => {
      if (typeof row[key] === 'object' && row[key] !== null) {
        return row[key].isRecord;
      }
      return false;
    });
  }, []);

  return (
    <>
      {editingTrappingResult ? (
        <EditTrapMonitoringRecordsModal
          propertiesStore={propertiesStore}
          editingTrappingResult={editingTrappingResult}
          setEditingTrappingResult={setEditingTrappingResult}
          traps={relevantTraps}
        />
      ) : null}
      {editingTrappingZoneSpray ? (
        <EditTrappingZoneSprayModal
          propertiesStore={propertiesStore}
          trappingZoneSpray={editingTrappingZoneSpray}
          setTrappingZoneSpray={setEditingTrappingZoneSpray}
        />
      ) : null}
      <Table bordered striped responsive size='sm'>
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.trapId}
                colSpan={column.colSpan ? column.colSpan : 1}
                style={column.style ? column.style : null}
              >
                {column.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tableData.map((row, index) => {
            const spray = row.spray;
            const hasSpray = spray !== undefined;
            const hasRecords = rowHasRecords(row);
            return (
              <tr key={index}>
                <td>{row.date}</td>
                <td>{row.week}</td>
                {hasSpray ? (
                  <Fragment>
                    <td colSpan={4 + 2 * relevantTraps.length}>
                      Spray Application{' '}
                      {<i>{spray.biofix === 1 && '(Biofix)'}</i>}:{' '}
                      <b>{spray.spray_product?.product?.name || '-'}</b>
                      {spray.comments ? (
                        <>
                          <br />
                          <i>{spray.comments}</i>
                        </>
                      ) : null}
                    </td>
                    <td>
                      <Button
                        variant='link'
                        size='sm'
                        onClick={() => setEditingTrappingZoneSpray(spray)}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </Button>
                      <ConfirmationModal
                        title='Confirm Delete'
                        bodyText='Are you sure you want to delete this spray?'
                        confirmAction={() => {
                          return deleteTrappingZoneSpray(spray.id);
                        }}
                        showButton={{
                          children: <FontAwesomeIcon icon={faTrash} />,
                          props: {
                            className: 'text-danger',
                            variant: 'link',
                            size: 'sm'
                          }
                        }}
                        confirmButton={{
                          props: { variant: 'danger' }
                        }}
                      />
                    </td>
                  </Fragment>
                ) : null}
                {hasRecords ? (
                  <>
                    <td>
                      {row.capChanges === 'some' ? (
                        <OverlayTrigger
                          placement='top'
                          overlay={<Tooltip>Some caps changed.</Tooltip>}
                        >
                          <FontAwesomeIcon
                            icon={faCheck}
                            className='text-warning'
                          />
                        </OverlayTrigger>
                      ) : row.capChanges === 'all' ? (
                        <OverlayTrigger
                          placement='top'
                          overlay={<Tooltip>All caps changed.</Tooltip>}
                        >
                          <FontAwesomeIcon
                            icon={faCheck}
                            className='text-success'
                          />
                        </OverlayTrigger>
                      ) : null}
                    </td>
                    <td>
                      {row.baseChanges === 'some' ? (
                        <OverlayTrigger
                          placement='top'
                          overlay={<Tooltip>Some bases changed.</Tooltip>}
                        >
                          <FontAwesomeIcon
                            icon={faCheck}
                            className='text-warning'
                          />
                        </OverlayTrigger>
                      ) : row.baseChanges === 'all' ? (
                        <OverlayTrigger
                          placement='top'
                          overlay={<Tooltip>All bases changed.</Tooltip>}
                        >
                          <FontAwesomeIcon
                            icon={faCheck}
                            className='text-success'
                          />
                        </OverlayTrigger>
                      ) : null}
                    </td>
                    <td
                      className={
                        row.threshold.sprayed ? 'text-info' : 'text-danger'
                      }
                    >
                      {row.threshold.amountThresholdsMet > 0 ||
                      row.threshold.accumulationThresholdsMet > 0 ||
                      row.threshold.averageThresholdMet ? (
                        <OverlayTrigger
                          placement='top'
                          overlay={
                            <Tooltip>
                              {row.threshold.sprayed ? (
                                <Row>
                                  <Col>
                                    Previous spray covers these thresholds.
                                  </Col>
                                </Row>
                              ) : null}
                              {row.threshold.amountThresholdsMet > 0 ? (
                                <Row>
                                  <Col>
                                    {row.threshold.amountThresholdsMet} x Single
                                    trap catch threshold.
                                  </Col>
                                </Row>
                              ) : null}
                              {row.threshold.accumulationThresholdsMet > 0 ? (
                                <Row>
                                  <Col>
                                    {row.threshold.accumulationThresholdsMet} x
                                    accumulation threshold.
                                  </Col>
                                </Row>
                              ) : null}
                              {row.threshold.averageThresholdMet ? (
                                <Row>
                                  <Col>Average threshold.</Col>
                                </Row>
                              ) : null}
                            </Tooltip>
                          }
                        >
                          <FontAwesomeIcon icon={faExclamationCircle} />
                        </OverlayTrigger>
                      ) : null}
                    </td>
                    {columns
                      .filter((column) => column.trapId !== undefined)
                      .map((column, index) => {
                        const record =
                          row[`${column.trapName}-${column.trapId}`];
                        return (
                          <Fragment key={index}>
                            <td
                              className={
                                record.amountThresholdMet ? 'text-danger' : null
                              }
                              style={{ borderLeft: '2px solid #dee2e6' }}
                            >
                              {record.amount === null ? '-' : record.amount}
                            </td>
                            <td
                              className={`${
                                record.accumulationThresholdMet
                                  ? 'text-danger'
                                  : null
                              } ${record.comments ? 'pr-0' : null}`}
                            >
                              <OverlayTrigger
                                placement='top'
                                overlay={
                                  <Tooltip>Weekly Catch Accumulation</Tooltip>
                                }
                              >
                                <i
                                  className={`${
                                    record.thresholdMet &&
                                    record.accumulationThresholdMet
                                      ? 'text-danger'
                                      : 'text-muted'
                                  }`}
                                >
                                  {record.accumulation === null
                                    ? '-'
                                    : `(${record.accumulation})`}
                                </i>
                              </OverlayTrigger>
                              {record.comments ? (
                                <OverlayTrigger
                                  placement='top'
                                  overlay={<Tooltip>{record.comments}</Tooltip>}
                                >
                                  <FontAwesomeIcon
                                    className='text-primary ml-2'
                                    icon={faComment}
                                  />
                                </OverlayTrigger>
                              ) : null}
                            </td>
                          </Fragment>
                        );
                      })}
                    <td
                      className={
                        row.threshold.averageThresholdMet ? 'text-danger' : null
                      }
                      style={{ borderLeft: '2px solid #dee2e6' }}
                    >
                      {row.averageAmount ? row.averageAmount : '-'}
                    </td>
                    <td>
                      <Button
                        variant='link'
                        size='sm'
                        onClick={() =>
                          setEditingTrappingResult(row.trappingResult)
                        }
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </Button>
                      <ConfirmationModal
                        title='Confirm Delete'
                        bodyText='Are you sure you want to delete this trapping result?'
                        confirmAction={() => {
                          return deleteTrappingResult(row.trappingResult);
                        }}
                        showButton={{
                          children: <FontAwesomeIcon icon={faTrash} />,
                          props: {
                            className: 'text-danger',
                            variant: 'link',
                            size: 'sm'
                          }
                        }}
                        confirmButton={{
                          props: { variant: 'danger' }
                        }}
                      />
                    </td>
                  </>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </>
  );
};

const TrappingResultsTablePrinted = ({
  trappingZoneResults,
  trappingZone,
  selectedInsectGroup
}) => {
  const { traplogStore } = useStores();
  const getRelevantTraps = useRelevantTraps();
  const relevantTraps = getRelevantTraps(trappingZone, selectedInsectGroup);

  const trappingResults = trappingZoneResults.results;

  const defaultColumnsPerPage = 12;

  const columns = [
    {
      title: 'Date'
    },
    { title: 'Week' },
    { title: 'Cap' },
    { title: 'Base' },
    {
      title: (
        <>
          TH
          <OverlayTrigger
            placement='top'
            overlay={<Tooltip>Threshold</Tooltip>}
          >
            <FontAwesomeIcon icon={faInfoCircle} className='ml-2' size='xs' />
          </OverlayTrigger>
        </>
      )
    },
    ...relevantTraps.map((trap) => ({
      title: trap.code,
      trapId: trap.id,
      trapName: trap.name,
      trapCode: trap.code,
      colSpan: 2,
      style: { borderLeft: '2px solid #dee2e6' }
    })),
    {
      title: (
        <>
          Avg/Week{' '}
          <OverlayTrigger
            placement='top'
            overlay={
              <Tooltip>
                The average number of insects per week across all traps.
              </Tooltip>
            }
          >
            <FontAwesomeIcon icon={faInfoCircle} className='ml-2' size='xs' />
          </OverlayTrigger>
        </>
      ),
      style: { borderLeft: '2px solid #dee2e6' }
    }
  ];

  const pageColumns = useMemo(() => {
    const pageColumns = [];
    let pageColumn = [];
    columns.forEach((column) => {
      pageColumn.push(column);
      if (pageColumn.length === defaultColumnsPerPage) {
        pageColumns.push([...pageColumn]);
        pageColumn = [];
      }
    });
    // Add any remaining columns to last page
    if (pageColumn.length > 0) {
      pageColumns.push([...pageColumn]);
    }
    return pageColumns;
  }, [columns]);

  const tableData = useMemo(() => {
    // Helper functions
    const formatDate = (dt) => ({
      date: dt.format('DD/MM/YYYY'),
      week: dt.format('w')
    });

    const calculateChangeStatus = (changedCount, totalTraps) => {
      if (changedCount === 0) return 'none';
      return changedCount < totalTraps ? 'some' : 'all';
    };

    const calculateAverageAmount = (amounts) => {
      if (!amounts.length) return null;
      const sum = amounts.reduce((acc, val) => acc + val, 0);
      return (sum / amounts.length).toFixed(2);
    };

    const processTraps = (trappingResult, traplogStore) => {
      const amounts = [];
      let amountThresholdsMet = 0;
      let accumulationThresholdsMet = 0;

      const traps = traplogStore.traps.map((trap) => {
        const monitoringRecord = trappingResult.trap_monitoring_records.find(
          (record) => record.trap.id === trap.id
        );

        const trapData = trappingResult.traps[trap.id] || {};
        const { amount = null, accumulation = null } = trapData;

        const amountThresholdMet =
          amount &&
          traplogStore.selectedInsectGroup.per_trap_catch_threshold !== null &&
          amount >= traplogStore.selectedInsectGroup.per_trap_catch_threshold;

        const accumulationThresholdMet =
          accumulation &&
          traplogStore.selectedInsectGroup.accumulation_threshold !== null &&
          accumulation >=
            calcAccumulationThreshold(
              traplogStore.selectedInsectGroup,
              moment(trappingResult.date)
            );

        if (amountThresholdMet) amountThresholdsMet++;
        if (accumulationThresholdMet) accumulationThresholdsMet++;

        amounts.push(amount);

        return {
          id: trap.id,
          name: trap.name,
          code: trap.code,
          amount,
          accumulation,
          comments: monitoringRecord?.comments || null,
          amountThresholdMet,
          accumulationThresholdMet
        };
      });

      return { traps, amounts, amountThresholdsMet, accumulationThresholdsMet };
    };

    // Main processing
    const results = trappingResults.map((trappingResult) => {
      const dt = moment(trappingResult.date);
      const dateInfo = formatDate(dt);

      const { traps, amounts, amountThresholdsMet, accumulationThresholdsMet } =
        processTraps(trappingResult, traplogStore);

      const averageAmount = calculateAverageAmount(amounts);
      const averageThresholdMet =
        traplogStore.selectedInsectGroup.average_threshold !== null &&
        averageAmount >= traplogStore.selectedInsectGroup.average_threshold;

      const luresChanged = trappingResult.trap_monitoring_records.filter(
        (record) => record.lure_changed
      ).length;

      const basesChanged = trappingResult.trap_monitoring_records.filter(
        (record) => record.base_changed
      ).length;

      return {
        ...dateInfo,
        trappingResult,
        capChanges: calculateChangeStatus(luresChanged, traps.length),
        baseChanges: calculateChangeStatus(basesChanged, traps.length),
        sprays: trappingResult.trapping_zone_sprays.map((spray) => ({
          ...dateInfo,
          ...spray
        })),
        record:
          trappingResult.trap_monitoring_records.length > 0
            ? {
                ...dateInfo,
                traps,
                luresChanged,
                basesChanged,
                amountThresholdsMet,
                accumulationThresholdsMet,
                averageThresholdMet,
                averageAmount,
                thresholdMet: trappingResult.threshold_met
              }
            : null,
        sprayed: trappingResult.sprayed
      };
    });

    // Format final page data
    return results.flatMap((result) => {
      const output = [];

      if (result.sprays.length > 0) {
        output.push(
          ...result.sprays.map((spray) => ({
            date: result.date,
            week: result.week,
            spray
          }))
        );
      }

      if (result.record) {
        const trapRecords = {};
        result.record.traps.forEach((trap) => {
          const record = {
            isRecord: true,
            amount: trap.amount,
            accumulation: trap.accumulation,
            comments: trap.comments
          };
          trapRecords[`${trap.name}-${trap.id}`] = record;
        });
        output.push({
          trappingResult: result.trappingResult,
          date: result.date,
          week: result.week,
          capChanges: result.capChanges,
          baseChanges: result.baseChanges,
          threshold: {
            sprayed: result.sprayed,
            amountThresholdsMet: result.record.amountThresholdsMet,
            accumulationThresholdsMet: result.record.accumulationThresholdsMet,
            averageThresholdMet: result.record.averageThresholdMet
          },
          ...trapRecords,
          averageAmount: {
            averageAmount: result.record.averageAmount,
            averageThresholdMet: result.record.averageThresholdMet
          }
        });
      }
      return output;
    });
  }, [trappingResults, relevantTraps, selectedInsectGroup]);

  const pageData = useMemo(() => {
    const tableDatas = [];
    let currentData = [];
    let currentRow = {};
    for (let i = 0; i < tableData.length; i++) {
      const row = tableData[i];
      const rowKeys = Object.keys(row);
      rowKeys.forEach((key, index) => {
        const value = row[key];
        // Don't add 'trappingResult' as not a column
        // Is used for the modal data.
        if (key !== 'trappingResult') {
          currentRow[key] = value;
        }
        if (
          Object.keys(currentRow).length === defaultColumnsPerPage ||
          rowKeys.length - 1 === index
        ) {
          currentData.push(currentRow);
          currentRow = {};
        }
      });
      tableDatas.push(currentData);
      currentData = [];
    }
    const pages = [];
    const maxLength = Math.max(...tableDatas.map((arr) => arr.length));
    for (let i = 0; i < maxLength; i++) {
      const group = tableDatas.map((arr) => (i < arr.length ? arr[i] : null));
      pages.push(group);
    }
    return pages;
  }, [tableData]);

  const rowHasRecords = useCallback((row) => {
    return Object.keys(row).some((key) => {
      if (typeof row[key] === 'object' && row[key] !== null) {
        return row[key].isRecord;
      }
      return false;
    });
  }, []);

  return pageData.map((page, pageIndex) => {
    const pageColumn = pageColumns[pageIndex];
    return (
      <div
        className={
          pageIndex > 0 ? 'page-break table-container' : 'table-container'
        }
      >
        <Table bordered striped responsive size='sm'>
          <thead>
            <tr>
              {pageColumn.map((column) => (
                <th
                  key={column.trapId}
                  colSpan={column.colSpan ? column.colSpan : 1}
                  style={column.style ? column.style : null}
                >
                  {column.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {page.map((row, index) => {
              if (row === null) {
                const colSpan = Object.keys(pageColumn).reduce((acc, key) => {
                  const value = pageColumn[key];
                  return acc + (value.colSpan !== undefined ? 2 : 1);
                }, 0);
                return (
                  <tr key={index}>
                    <td colSpan={colSpan}></td>
                  </tr>
                );
              }
              const spray = row.spray;
              const hasSpray = spray !== undefined;
              const hasRecords = rowHasRecords(row);
              return (
                <tr key={index}>
                  {row.date ? <td>{row.date}</td> : null}
                  {row.week ? <td>{row.week}</td> : null}
                  {hasSpray ? (
                    <Fragment>
                      <td colSpan={4 + 2 * relevantTraps.length}>
                        Spray Application{' '}
                        {<i>{spray.biofix === 1 && '(Biofix)'}</i>}:{' '}
                        <b>{spray.spray_product?.product?.name || '-'}</b>
                        {spray.comments ? (
                          <>
                            <br />
                            <i>{spray.comments}</i>
                          </>
                        ) : null}
                      </td>
                    </Fragment>
                  ) : null}
                  {hasRecords ? (
                    <>
                      {row.capChanges ? (
                        <td>
                          {row.capChanges === 'some' ? (
                            <OverlayTrigger
                              placement='top'
                              overlay={<Tooltip>Some caps changed.</Tooltip>}
                            >
                              <FontAwesomeIcon
                                icon={faCheck}
                                className='text-warning'
                              />
                            </OverlayTrigger>
                          ) : row.capChanges === 'all' ? (
                            <OverlayTrigger
                              placement='top'
                              overlay={<Tooltip>All caps changed.</Tooltip>}
                            >
                              <FontAwesomeIcon
                                icon={faCheck}
                                className='text-success'
                              />
                            </OverlayTrigger>
                          ) : null}
                        </td>
                      ) : null}
                      {row.baseChanges ? (
                        <td>
                          {row.baseChanges === 'some' ? (
                            <OverlayTrigger
                              placement='top'
                              overlay={<Tooltip>Some bases changed.</Tooltip>}
                            >
                              <FontAwesomeIcon
                                icon={faCheck}
                                className='text-warning'
                              />
                            </OverlayTrigger>
                          ) : row.baseChanges === 'all' ? (
                            <OverlayTrigger
                              placement='top'
                              overlay={<Tooltip>All bases changed.</Tooltip>}
                            >
                              <FontAwesomeIcon
                                icon={faCheck}
                                className='text-success'
                              />
                            </OverlayTrigger>
                          ) : null}
                        </td>
                      ) : null}
                      {row.threshold ? (
                        <td
                          className={
                            row.threshold.sprayed ? 'text-info' : 'text-danger'
                          }
                        >
                          {row.threshold.amountThresholdsMet > 0 ||
                          row.threshold.accumulationThresholdsMet > 0 ||
                          row.threshold.averageThresholdMet ? (
                            <OverlayTrigger
                              placement='top'
                              overlay={
                                <Tooltip>
                                  {row.threshold.sprayed ? (
                                    <Row>
                                      <Col>
                                        Previous spray covers these thresholds.
                                      </Col>
                                    </Row>
                                  ) : null}
                                  {row.threshold.amountThresholdsMet > 0 ? (
                                    <Row>
                                      <Col>
                                        {row.threshold.amountThresholdsMet} x
                                        Single trap catch threshold.
                                      </Col>
                                    </Row>
                                  ) : null}
                                  {row.threshold.accumulationThresholdsMet >
                                  0 ? (
                                    <Row>
                                      <Col>
                                        {
                                          row.threshold
                                            .accumulationThresholdsMet
                                        }{' '}
                                        x accumulation threshold.
                                      </Col>
                                    </Row>
                                  ) : null}
                                  {row.threshold.averageThresholdMet ? (
                                    <Row>
                                      <Col>Average threshold.</Col>
                                    </Row>
                                  ) : null}
                                </Tooltip>
                              }
                            >
                              <FontAwesomeIcon icon={faExclamationCircle} />
                            </OverlayTrigger>
                          ) : null}
                        </td>
                      ) : null}
                      {columns
                        .filter((column) => column.trapId !== undefined)
                        .filter((column) =>
                          Object.keys(row).includes(
                            `${column.trapName}-${column.trapId}`
                          )
                        )
                        .map((column, index) => {
                          const record =
                            row[`${column.trapName}-${column.trapId}`];
                          return (
                            <Fragment key={index}>
                              <td
                                className={
                                  record.amountThresholdMet
                                    ? 'text-danger'
                                    : null
                                }
                                style={{ borderLeft: '2px solid #dee2e6' }}
                              >
                                {record.amount === null ? '-' : record.amount}
                              </td>
                              <td
                                className={`${
                                  record.accumulationThresholdMet
                                    ? 'text-danger'
                                    : null
                                } ${record.comments ? 'pr-0' : null}`}
                              >
                                <i
                                  className={`${
                                    record.thresholdMet &&
                                    record.accumulationThresholdMet
                                      ? 'text-danger'
                                      : 'text-muted'
                                  }`}
                                >
                                  {record.accumulation === null
                                    ? '-'
                                    : `(${record.accumulation})`}
                                </i>
                                {record.comments ? (
                                  <FontAwesomeIcon
                                    className='text-primary ml-2'
                                    icon={faComment}
                                  />
                                ) : null}
                              </td>
                            </Fragment>
                          );
                        })}
                    </>
                  ) : null}
                  {row.averageAmount ? (
                    <td
                      className={
                        row.averageAmount.averageThresholdMet
                          ? 'text-danger'
                          : null
                      }
                      style={{ borderLeft: '2px solid #dee2e6' }}
                    >
                      {row.averageAmount.averageAmount
                        ? row.averageAmount.averageAmount
                        : '-'}
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    );
  });
};

function TrappingZoneResults(props) {
  return (
    <div className='traplog'>
      <div className={'screen-only-view'}>
        <TrappingResultsTable
          trappingZoneResults={props.trappingZoneResults}
          trappingZone={props.trappingZone}
          selectedInsectGroup={props.selectedInsectGroup}
          propertiesStore={props.propertiesStore}
        />
      </div>
      {props.printable ? (
        <div className={'print-only-view'}>
          <TrappingResultsTablePrinted
            trappingZoneResults={props.trappingZoneResults}
            trappingZone={props.trappingZone}
            selectedInsectGroup={props.selectedInsectGroup}
          />
        </div>
      ) : null}
    </div>
  );
}

TrappingZoneResults.propTypes = {
  trappingZone: PropTypes.object.isRequired,
  trappingZoneResults: PropTypes.object.isRequired,
  selectedInsectGroup: PropTypes.object.isRequired,
  propertiesStore: PropTypes.instanceOf(PropertiesStore).isRequired
};

export default observer(TrappingZoneResults);
